import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { Layout } from "./components/Layout";
import { DisclaimerGate } from "./components/DisclaimerGate";
import { RequireAuth } from "./components/RequireAuth";
import "./index.css";
import { Admin } from "./pages/Admin";
import { Evaluations } from "./pages/Evaluations";
import { Memory } from "./pages/Memory";
import { MemoryConsole } from "./pages/MemoryConsole";
import { AdminMail } from "./pages/AdminMail";
import { Billing } from "./pages/Billing";
import { Choose } from "./pages/Choose";
import { DataRooms } from "./pages/DataRooms";
import { Login } from "./pages/Login";
import { NewEngagement } from "./pages/NewEngagement";
import { Order } from "./pages/Order";
import { Pending } from "./pages/Pending";
import { Projects } from "./pages/Projects";
import { ReadDeliverable } from "./pages/ReadDeliverable";
import { RunDetail } from "./pages/RunDetail";
import { SignIn } from "./pages/SignIn";
import { Engagements } from "./pages/Engagements";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * `/runs/<id>` as it was, sent to `/engagements/<id>`.
 *
 * The id has to travel, which `<Navigate to="…">` cannot do — its target is a
 * literal. Worth the component: this path is in bookmarks, in the audit
 * trail's "open run" links, and in every support message that ever quoted a
 * run id.
 */
function LegacyRunRedirect() {
  const { runId } = useParams();
  const [params] = useSearchParams();
  const query = params.toString();
  return (
    <Navigate
      to={`/engagements/${encodeURIComponent(runId ?? "")}${query ? `?${query}` : ""}`}
      replace
    />
  );
}

const router = createBrowserRouter([
  // Outside the guard: the pages a visitor without a usable session is allowed
  // to see. `/login` is the landing page — sign-in and the case for the
  // product are the same page now, because the workflow tiles that used to be
  // out here are a choice, and a choice made before anybody knows whether you
  // may run anything is one that gets thrown away.
  { path: "/login", element: <Login /> },
  { path: "/pending", element: <Pending /> },
  // Where every emailed link opens. Outside the guard because the person
  // holding the link is, by definition, not signed in yet.
  { path: "/signin", element: <SignIn /> },
  // Kept because it was linked and bookmarked. It has no content of its own
  // any more.
  { path: "/welcome", element: <Navigate to="/login" replace /> },
  // One HTML deliverable at the window's width, with its Finetune panel —
  // where "Open in new tab" goes. Behind the same guards as the app, outside
  // its frame: the document is the page.
  {
    path: "/engagements/:runId/read/*",
    element: (
      <RequireAuth>
        <DisclaimerGate>
          <ReadDeliverable />
        </DisclaimerGate>
      </RequireAuth>
    ),
  },
  {
    path: "/",
    element: (
      // Inside the auth guard, not beside it: the terms are shown to a
      // *person*, and there is nobody to show them to until the session is
      // known good. The server refuses regardless — see
      // `api/auth/middleware.py` — so this is the notice, not the control.
      <RequireAuth>
        <DisclaimerGate>
          <Layout />
        </DisclaimerGate>
      </RequireAuth>
    ),
    children: [
      // The tiles are what you land on: pick the piece of work, then fill in
      // that playbook's own form.
      { index: true, element: <Choose /> },
      // "Engagement" is what the reader started; `run_id`, `/api/runs` and
      // `runs/` on disk keep the word the system is built on. `/runs` is kept
      // because it was linked and bookmarked — including from the audit trail.
      { path: "engagements", element: <Engagements /> },
      { path: "runs", element: <Navigate to="/engagements" replace /> },
      { path: "new/:playbook", element: <NewEngagement /> },
      // Choosing is the first step, so a bare `/new` is the choice.
      { path: "new", element: <Navigate to="/" replace /> },
      { path: "projects", element: <Projects /> },
      // The page went: the start page holds every playbook and the tile
      // that writes a new one. Kept as an address because it was bookmarked.
      { path: "playbooks", element: <Navigate to="/" replace /> },
      { path: "data-rooms", element: <DataRooms /> },
      { path: "memory", element: <Memory /> },
      { path: "billing", element: <Billing /> },
      { path: "admin", element: <Admin /> },
      { path: "admin/evaluations", element: <Evaluations /> },
      { path: "admin/memory", element: <MemoryConsole /> },
      { path: "admin/mail", element: <AdminMail /> },
      { path: "engagements/:runId", element: <RunDetail /> },
      { path: "runs/:runId", element: <LegacyRunRedirect /> },
      // Where an emailed proposal's links open. Not in the nav: it is reached
      // from a mail, and the reply is how most people order.
      { path: "order/:id", element: <Order /> },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>,
);
